import { cancelMediaSwap, prefetchSwapImages, swapMedia } from '@theme/media-swap';
import {
  applySelectorMode,
  queueSelectorMode,
  resolveSelectorMode,
  unwatchSelectorMode,
  watchSelectorMode,
} from '@theme/variant-selector-mode';

function visibleVariantPanel(card) {
  return [...card.querySelectorAll('[data-product-details-upsell-variant-panel]')].find(
    (panel) => panel.getClientRects().length > 0,
  );
}

/** Product Details upsell enhancement. */

class DieuxProductDetailsUpsellCard extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.controller = new AbortController();
    this.productForm = this.querySelector('product-form-component');
    this.cartMediaTemplate = this.productForm?.querySelector('template[data-cart-line-media]');

    this.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (input instanceof HTMLInputElement && input.matches('[data-product-details-upsell-variant]')) {
          this.applyVariant(input);
          return;
        }

        if (input instanceof HTMLInputElement && input.matches('[data-product-details-upsell-choice]')) {
          this.selectVariant(input.value, { focusChoice: true });
          return;
        }

        if (input instanceof HTMLSelectElement && input.matches('[data-product-details-upsell-select]')) {
          const selectedOption = input.options[input.selectedIndex];
          this.selectVariant(selectedOption?.dataset.variantId || '', { restoreSelectFocus: true });
        }
      },
      { signal: this.controller.signal },
    );

    watchSelectorMode(
      this,
      () => this.updateVariantSelectorMode(),
      '[data-product-details-upsell-selector]',
    );
    const selectedVariant = this.selectedVariant;
    if (selectedVariant) this.applyVariant(selectedVariant);
    this.mediaReady = true;
    queueSelectorMode(this);
    prefetchSwapImages(
      this.querySelectorAll('[data-product-details-upsell-variant-panel] img[loading="lazy"]'),
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = undefined;
    this.mediaReady = false;
    if (this.mediaFrame) cancelMediaSwap(this.mediaFrame);
    unwatchSelectorMode(this);
  }

  get selectedVariant() {
    return this.querySelector('[data-product-details-upsell-variant]:checked');
  }

  /**
   * Carry the card's image across a variant change with the product card's layered swap.
   *
   * @param {HTMLInputElement} input - Variant radio that just became checked.
   */
  swapVariantMedia(input) {
    const panel = input.nextElementSibling;
    const nextFrame =
      panel instanceof HTMLElement && panel.matches('[data-product-details-upsell-variant-panel]')
        ? panel.querySelector('[data-product-details-upsell-media-frame]')
        : null;
    const previousFrame = this.mediaFrame;
    this.mediaFrame = nextFrame;

    if (!this.mediaReady || !nextFrame || !previousFrame || previousFrame === nextFrame) return;

    cancelMediaSwap(previousFrame);
    const outgoing = previousFrame.querySelector(':scope > img');
    const incoming = nextFrame.querySelector(':scope > img');
    if (!outgoing || !incoming) return;

    swapMedia(nextFrame, incoming, {
      outgoing,
      retainOnAbort: 'incoming',
      release: (layer) => previousFrame.append(layer),
    });
  }

  applyVariant(input) {
    if (!(input instanceof HTMLInputElement) || !this.productForm) return;

    this.swapVariantMedia(input);
    const variantId = input.value;
    this.productForm.dataset.currentVariantId = variantId;
    this.productForm.dataset.variantPrice = input.dataset.variantPrice || '';
    this.productForm.dataset.productUrl = input.dataset.productUrl || '';
    this.productForm.dataset.cartLineDiscountPricing = input.dataset.cartLineDiscountPricing || '';

    if (input.dataset.cartLineSubtitle) {
      this.productForm.dataset.cartLineSubtitle = input.dataset.cartLineSubtitle;
    } else {
      delete this.productForm.dataset.cartLineSubtitle;
    }

    const mediaSource = this.querySelector(
      `template[data-product-details-upsell-media="${CSS.escape(variantId)}"]`,
    );
    if (this.cartMediaTemplate && mediaSource instanceof HTMLTemplateElement) {
      this.cartMediaTemplate.innerHTML = mediaSource.innerHTML;
    }

    this.querySelectorAll('[data-product-details-upsell-option]').forEach((option) => {
      if (!(option instanceof HTMLLabelElement)) return;
      option.toggleAttribute('data-selected', option.htmlFor === input.id);
    });

    this.querySelectorAll('[data-product-details-upsell-choice]').forEach((choice) => {
      if (choice instanceof HTMLInputElement) choice.checked = choice.defaultChecked;
    });

    this.querySelectorAll('[data-product-details-upsell-select]').forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      const matchingOption = [...select.options].find(
        (option) => option.dataset.variantId === variantId,
      );
      if (matchingOption) select.value = matchingOption.value;
    });
    queueSelectorMode(this);
  }

  selectVariant(variantId, { restoreSelectFocus = false, focusChoice = false } = {}) {
    const value = CSS.escape(String(variantId));
    const input = this.querySelector(`[data-product-details-upsell-variant][value="${value}"]`);
    if (!(input instanceof HTMLInputElement)) return;

    input.checked = true;
    this.applyVariant(input);

    if (focusChoice) {
      const choice = input.nextElementSibling?.querySelector(
        `[data-product-details-upsell-choice][value="${value}"]`,
      );
      if (choice instanceof HTMLInputElement) choice.focus({ preventScroll: true });
    }

    if (restoreSelectFocus) {
      requestAnimationFrame(() => {
        const panel = visibleVariantPanel(this);
        const select = panel?.querySelector('[data-product-details-upsell-select]:not([hidden])');
        if (select instanceof HTMLSelectElement) select.focus({ preventScroll: true });
      });
    }
  }

  updateVariantSelectorMode() {
    const panel = visibleVariantPanel(this);
    const visibleSelector = panel?.querySelector('[data-product-details-upsell-selector]');
    if (!(visibleSelector instanceof HTMLElement)) return;

    const mode = resolveSelectorMode(visibleSelector, '[data-product-details-upsell-option]');
    if (!mode) return;

    this.querySelectorAll('[data-product-details-upsell-selector]').forEach((selector) => {
      if (!(selector instanceof HTMLElement)) return;
      const select = selector.querySelector('[data-product-details-upsell-select]');
      if (!(select instanceof HTMLSelectElement)) return;

      applySelectorMode(selector, select, mode);
    });
  }
}

class DieuxProductDetailsUpsells extends HTMLElement {
  connectedCallback() {
    if (this.controller || !this.dataset.recommendationsUrl) return;

    this.controller = new AbortController();
    const load = () => this.loadRecommendations();

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer?.disconnect();
        this.observer = undefined;
        load();
      },
      { rootMargin: '200px 0px' },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = undefined;
    this.controller?.abort();
    this.controller = undefined;
  }

  async loadRecommendations() {
    if (this.dataset.loading === 'true') return;
    this.dataset.loading = 'true';

    try {
      const response = await fetch(this.dataset.recommendationsUrl, {
        signal: this.controller?.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error('Recommendations request failed');

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const blockId = this.dataset.productDetailsUpsells || '';
      const source = documentFragment.querySelector(
        `dieux-product-details-upsells[data-product-details-upsells="${CSS.escape(blockId)}"]`,
      );
      const sourceContent = source?.querySelector('[data-product-details-upsells-content]');
      const targetContent = this.querySelector('[data-product-details-upsells-content]');

      if (!sourceContent || !targetContent || !sourceContent.children.length) {
        this.hidden = true;
        return;
      }

      targetContent.setAttribute('data-variant-selectors-pending', '');
      try {
        targetContent.innerHTML = sourceContent.innerHTML;
      } finally {
        window.DieuxFrame.write(() => targetContent.removeAttribute('data-variant-selectors-pending'));
      }
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (error.name !== 'AbortError') this.hidden = true;
    } finally {
      delete this.dataset.loading;
    }
  }
}

if (!customElements.get('dieux-product-details-upsell-card')) {
  customElements.define('dieux-product-details-upsell-card', DieuxProductDetailsUpsellCard);
}

if (!customElements.get('dieux-product-details-upsells')) {
  customElements.define('dieux-product-details-upsells', DieuxProductDetailsUpsells);
}
